/**
 * Pantalla de acceso, mínima — mismo patrón que el portal de Orvian/aviron.
 *
 * Divulgación progresiva: cada paso muestra UN campo, nunca dos. Primero el
 * correo, después la contraseña y, solo si la cuenta lo exige, el código de dos
 * pasos. Sin tarjeta ni marcos: la marca arriba, el campo y el botón.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, setTokens } from '../lib/api';
import { MEET_URL, olvidarSalto, rutaDeVueltaMeetValida, rutaDeVueltaValida } from '../lib/dominios';
import { useStore } from '../store/useStore';
import { toast } from 'sonner';
import { Loader2, KeyRound, Lock, Eye, EyeOff } from 'lucide-react';
import { mensajeErrorAcceso } from '../lib/erroresAcceso';
import { loginPasskey, passkeySupported } from '../lib/webauthn';

const CAMPO = {
  width: '100%',
  borderRadius: 14,
  border: '1px solid var(--line)',
  background: 'var(--panel-2)',
  padding: '14px 20px',
  fontSize: 15,
  color: 'var(--text)',
  fontFamily: 'inherit',
  // Sin `outline: none`: como estilo en línea ganaba a la regla `:focus-visible` de index.css y
  // dejaba los dos campos del único formulario obligatorio de la aplicación sin marca de foco.
};

/** Etiqueta del campo: pequeña y encima. El placeholder solo no valía —desaparece al escribir,
 *  así que ni se puede releer la instrucción ni comprobar qué se está capturando. */
const ETIQUETA = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-2)',
  paddingLeft: 4,
};

/** Código de seis casillas con UN input invisible detrás: así pegar el código
 *  completo funciona a la primera y no hay que perseguir el foco entre seis. */
function CampoOtp({ valor, alCambiar, inputRef }) {
  const activa = Math.min(valor.length, 5);
  return (
    // El nombre va en el input (era del grupo y al lector le llegaba un «editar texto» sin más) y el
    // grupo dibuja el foco aunque ya estén los 6 dígitos (S11).
    <div className="relative rounded-xl focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-600" onClick={() => inputRef.current?.focus()} role="group">
      <input
        ref={inputRef}
        aria-label="Código de seis dígitos de tu app de autenticación"
        value={valor}
        onChange={(e) => alCambiar(e.target.value.replace(/\D/g, '').slice(0, 6))}
        autoComplete="one-time-code"
        inputMode="numeric"
        data-testid="login-totp"
        className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
      />
      <div className="flex justify-between gap-2" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="flex flex-1 items-center justify-center font-semibold"
            style={{
              height: 52,
              borderRadius: 12,
              fontSize: 20,
              color: 'var(--text)',
              border: `1px solid ${i === activa && valor.length < 6 ? 'var(--accent)' : 'var(--line)'}`,
              background: i === activa && valor.length < 6
                ? 'color-mix(in srgb, var(--accent) 7%, transparent)'
                : valor[i] ? 'var(--panel-2)' : 'var(--bg)',
              transition: 'border-color .15s, background .15s',
            }}
          >
            {valor[i] ?? ''}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Login() {
  const nav = useNavigate();
  const setUser = useStore((s) => s.setUser);

  // Desde el aviso «tu proveedor rechazó la contraseña» (AppShell): el correo ya
  // viene escrito y se pasa directo a la contraseña, explicando por qué.
  const [reentrada] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const c = (q.get('correo') || '').trim();
      return q.get('motivo') === 'credenciales' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c) ? c : null;
    } catch (_) { return null; }
  });
  // La sesión caducó o se cerró desde otro equipo (lib/api.js → irAlLogin).
  const [sesionTerminada] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('motivo') === 'sesion'; } catch (_) { return false; }
  });
  const [paso, setPaso] = useState(reentrada ? 'password' : 'correo');   // correo | password | mfa
  const [correo, setCorreo] = useState(reentrada || '');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pkLoading, setPkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [verPassword, setVerPassword] = useState(false);
  const [mayusculas, setMayusculas] = useState(false);
  const alTeclear = (e) => {
    if (typeof e.getModifierState === 'function') setMayusculas(e.getModifierState('CapsLock'));
  };

  // El foco sigue al paso: cambiar de campo y tener que tocarlo a mano es
  // justo el tropiezo que un acceso de un campo debe evitar.
  const campoRef = useRef(null);
  useEffect(() => { campoRef.current?.focus(); }, [paso]);

  const volverAlInicio = () => {
    setPaso('correo'); setPassword(''); setCodigo(''); setError(null);
  };

  // Orvian Meet manda aquí con ?meet=/abc-defg-hij para entrar con la cuenta y volver a la reunión.
  // Con la sesión ya abierta no hay nada que pedir: se vuelve a Meet directamente.
  const volverMeet = (() => {
    try {
      const r = new URLSearchParams(window.location.search).get('meet');
      return rutaDeVueltaMeetValida(r) ? r : null;
    } catch (_) { return null; }
  })();
  const yaDentro = useStore((s) => !!s.user);
  useEffect(() => {
    if (volverMeet && yaDentro) window.location.replace(`${MEET_URL}${volverMeet}`);
    // Con la sesión ya abierta, /login no tiene nada que pedir (p. ej. una dirección guardada en el navegador)
    else if (yaDentro) nav('/mail', { replace: true });
  }, [volverMeet, yaDentro]); // eslint-disable-line react-hooks/exhaustive-deps

  const entrar = (data, msg) => {
    setTokens(data.access_token, data.refresh_token);
    setUser(data.user);
    toast.success(msg);
    if (volverMeet) {
      window.location.assign(`${MEET_URL}${volverMeet}`);
      return;
    }
    // Si llegamos aquí desde el clic en una notificación con la sesión caducada,
    // `Protected` dejó apuntado el correo concreto: vamos ahí, no a la bandeja.
    let destino = '/mail';
    try {
      destino = sessionStorage.getItem('zuno_next') || '/mail';
      sessionStorage.removeItem('zuno_next');
    } catch (_) {}
    // Un formulario de docs.orvian.mx que pide sesión manda aquí con ?volver=/f/<token>
    try {
      const volver = new URLSearchParams(window.location.search).get('volver');
      if (rutaDeVueltaValida(volver)) destino = volver;
    } catch (_) {}
    // Recién entrado, un documento pendiente puede volver a docs.orvian.mx aunque hace un momento
    // se hubiera abierto aquí (el freno de bucles de lib/dominios.js es para sesiones que no llegan)
    olvidarSalto();
    nav(destino);
  };

  const passkey = async () => {
    setPkLoading(true);
    try {
      entrar(await loginPasskey(), 'Bienvenido');
    } catch (err) {
      toast.error(err.response?.data?.detail
        || (err.name === 'NotAllowedError' ? 'Operación cancelada' : 'No se pudo iniciar con passkey'));
    } finally { setPkLoading(false); }
  };

  const alEnviar = async (e) => {
    e.preventDefault();
    setError(null);
    const escrito = correo.trim();

    if (paso === 'correo') {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(escrito)) { setPaso('password'); return; }
      setError('Escribe tu correo corporativo.');
      return;
    }

    setEnviando(true);
    try {
      const cuerpo = { email: escrito, password };
      if (paso === 'mfa') cuerpo.totp = codigo.trim();
      const res = await api.post('/auth/login', cuerpo);
      if (res.data.requires_2fa) {
        setPaso('mfa'); setEnviando(false);
        if (paso === 'mfa') setError('Código incorrecto.');
        return;
      }
      entrar(res.data, 'Bienvenido a Orvian');
    } catch (err) {
      setError(mensajeErrorAcceso(err, paso));
      setEnviando(false);
    }
  };

  const etiquetaBoton = enviando
    ? 'Un momento…'
    : paso === 'correo' ? 'Continuar' : paso === 'password' ? 'Entrar' : 'Verificar';

  return (
    <div className="flex min-h-screen flex-col" style={{ background: 'var(--bg)' }}>
      <main className="flex flex-1 items-center justify-center px-6 py-10">
        <div className="w-full" style={{ maxWidth: 380 }}>
          {/* --- Marca --- */}
          <div className="mb-12 flex flex-col items-center text-center" role="img" aria-label="Orvian">
            <img
              src="/brand/wordmark-light.png"
              alt=""
              className="h-24 w-auto dark:hidden"
              draggable={false}
              data-testid="gmail-logo"
            />
            <img
              src="/brand/wordmark-dark.png"
              alt=""
              className="hidden h-24 w-auto dark:block"
              draggable={false}
            />
          </div>

          <form onSubmit={alEnviar} className="space-y-3">
            {sesionTerminada && paso === 'correo' && (
              <p data-testid="login-aviso-sesion" className="px-1" style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
                Tu sesión terminó: caducó o se cerró desde otro equipo. Vuelve a entrar y te
                llevamos a donde estabas.
              </p>
            )}

            {paso === 'correo' && (
              <div className="space-y-1.5">
                <label htmlFor="login-correo" style={ETIQUETA}>Correo corporativo</label>
                <input
                  id="login-correo"
                  ref={campoRef}
                  value={correo}
                  onChange={(e) => setCorreo(e.target.value)}
                  placeholder="nombre@tuempresa.mx"
                  autoComplete="username"
                  type="email"
                  required
                  data-testid="login-email"
                  style={CAMPO}
                />
              </div>
            )}

            {paso !== 'correo' && (
              <div
                className="flex items-center justify-between"
                style={{ borderRadius: 14, border: '1px solid var(--line)', background: 'var(--panel)', padding: '12px 20px' }}
              >
                <span className="truncate" style={{ fontSize: 14, color: 'var(--text-2)' }}>{correo.trim()}</span>
                <button
                  type="button"
                  onClick={volverAlInicio}
                  className="shrink-0 font-medium"
                  style={{ fontSize: 13, color: 'var(--accent)', background: 'none', border: 0, cursor: 'pointer' }}
                >
                  Cambiar
                </button>
              </div>
            )}

            {paso === 'password' && reentrada && correo.trim() === reentrada && (
              <p data-testid="login-aviso-credenciales" className="px-1" style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
                Tu proveedor de correo ya no acepta la contraseña que teníamos guardada. Escribe la
                contraseña actual de tu buzón para reanudar el envío y la llegada de correo.
              </p>
            )}

            {paso === 'password' && (
              <div className="space-y-1.5">
                <label htmlFor="login-password" style={ETIQUETA}>Contraseña de tu buzón</label>
                <div className="relative">
                  <input
                    id="login-password"
                    ref={campoRef}
                    type={verPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={alTeclear}
                    onKeyUp={alTeclear}
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    data-testid="login-password"
                    style={{ ...CAMPO, paddingRight: 56 }}
                  />
                  {/* Las contraseñas del proveedor son largas y en el móvil se escriben mal con facilidad (A26). */}
                  <button
                    type="button"
                    onClick={() => { setVerPassword((v) => !v); campoRef.current?.focus(); }}
                    aria-pressed={verPassword}
                    aria-label={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={verPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    data-testid="login-password-ver"
                    className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center"
                    style={{ width: 44, height: 44, borderRadius: 12, background: 'none', border: 0, cursor: 'pointer', color: 'var(--text-2)' }}
                  >
                    {verPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                  </button>
                </div>
                {/* Siempre montado, cambia solo el texto: así los lectores de pantalla lo anuncian (S11) */}
                <p role="status" data-testid="login-bloq-mayus" className="px-1" style={{ fontSize: 13, color: 'var(--text-2)', minHeight: mayusculas ? undefined : 0 }}>
                  {mayusculas ? 'Bloq Mayús está activado.' : ''}
                </p>
              </div>
            )}

            {paso === 'mfa' && (
              <div className="space-y-2">
                <p className="text-center" style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  Escribe el código de tu app de autenticación
                </p>
                <CampoOtp valor={codigo} alCambiar={setCodigo} inputRef={campoRef} />
              </div>
            )}

            {error && (
              <p role="alert" className="px-1" style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</p>
            )}

            <button
              type="submit"
              disabled={enviando || (paso === 'mfa' && codigo.length < 6)}
              data-testid={paso === 'mfa' ? 'verify-2fa-login' : 'login-submit'}
              className="w-full font-semibold"
              style={{
                borderRadius: 9999,
                background: 'var(--accent)',
                color: '#fff',
                padding: '14px 20px',
                fontSize: 15,
                border: 0,
                cursor: enviando ? 'default' : 'pointer',
                opacity: enviando ? 0.6 : 1,
                transition: 'opacity .15s',
              }}
            >
              {enviando && <Loader2 className="inline h-4 w-4 mr-2 animate-spin" />}
              {etiquetaBoton}
            </button>

            {/* También en el paso del 2FA: quien perdió el teléfono se quedaba sin salida (A7). */}
            <p className="pt-1 text-center">
              <Link
                to="/ayuda-acceso"
                data-testid="login-forgot-password-link"
                style={{ fontSize: 13, color: 'var(--text-2)', textDecoration: 'none' }}
              >
                ¿No puedes entrar?
              </Link>
            </p>

            {paso === 'correo' && passkeySupported() && (
              <button
                type="button"
                onClick={passkey}
                disabled={pkLoading}
                data-testid="login-passkey"
                className="w-full font-semibold flex items-center justify-center gap-2"
                style={{
                  borderRadius: 9999,
                  background: 'transparent',
                  color: 'var(--text-2)',
                  padding: '13px 20px',
                  fontSize: 14,
                  border: '1px solid var(--line-2)',
                  cursor: 'pointer',
                }}
              >
                {pkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Usar passkey
              </button>
            )}
          </form>

          {/* --- Pie --- */}
          <p className="mt-8 flex items-center justify-center gap-1.5" style={{ fontSize: 11, color: 'var(--text-3)' }}>
            <Lock className="h-3 w-3" />
            Acceso protegido · conexión cifrada · auditoría de accesos
          </p>
        </div>
      </main>
    </div>
  );
}
